import ExerciseSupport from '../../../student/components/exercises/ExerciseSupport';
import HeadphonesRoundedIcon from '@mui/icons-material/HeadphonesRounded';
import { Box, Stack, Typography } from '@mui/material';
import { ListenRepeatExercise, shows } from '../../../student/mockLessonContent';
import { BLUE, cardSx, INTER, NAVY, POPPINS, RING_TRACK, SLATE, SURFACE_SOFT } from '../../../student/studentStyles';
import AudioPlayButton from '../../../student/components/AudioPlayButton';
import InfoExpander from '../../../student/components/exercises/InfoExpander';
import RecordAnswerPanel from '../../../student/components/exercises/RecordAnswerPanel';
import SpeakingHero from '../../../student/components/exercises/SpeakingHero';
import TeacherNote from '../../../student/components/exercises/TeacherNote';
import { ExerciseReady, useExerciseLayout } from './exerciseLayout';

interface ListenRepeatExerciseCardProps {
    exercise: ListenRepeatExercise;
    onReady: ExerciseReady;
}

// "Dialog w/ Listen & Speak" and its ungraded twin. Imitation: the target is
// handed to the student. Good passes; other scores offer Try again,
// with Skip after two unsuccessful takes. The
// ungraded twin still shows the score as feedback and completes on any take.
export default function ListenRepeatExerciseCard({ exercise, onReady }: ListenRepeatExerciseCardProps) {
    const desktop = useExerciseLayout() === 'desktop';
    const { target } = exercise;

    return (
        <Box sx={{ ...cardSx, overflow: 'hidden' }}>
            <SpeakingHero
                label={exercise.graded ? 'LISTEN & REPEAT' : 'LISTEN & REPEAT · SCORE IS FEEDBACK ONLY'}
                hanzi={target.hanzi}
                pinyin={shows(exercise, 'pinyin') ? target.pinyin : undefined}
            />

            <Box sx={{ p: desktop ? 3 : 2.5 }}>
                {exercise.teacher?.intro && <Box sx={{ mb: 2.5 }}><TeacherNote text={exercise.teacher.intro} /></Box>}
                <Box sx={{ display: desktop ? 'grid' : 'block', gridTemplateColumns: 'minmax(0, 1fr) minmax(280px, .9fr)', gap: 3, alignItems: 'start' }}>
                    <Box>
                        <Stack direction="row" alignItems="center" spacing={1.5}>
                            <Box sx={{
                                width: 42, height: 42, borderRadius: '14px', flexShrink: 0,
                                display: 'flex', alignItems: 'center', justifyContent: 'center', backgroundColor: `${BLUE}1A`,
                            }}>
                                <HeadphonesRoundedIcon sx={{ color: BLUE }} />
                            </Box>
                            <Box sx={{ flex: 1, minWidth: 0 }}>
                                <Typography sx={{ fontFamily: POPPINS, fontWeight: 600, fontSize: 16, color: NAVY }}>
                                    Model audio
                                </Typography>
                                <Typography sx={{ fontFamily: INTER, fontSize: 13, color: SLATE }}>
                                    Play it as often as you like
                                </Typography>
                            </Box>
                            <AudioPlayButton ariaLabel={`Play audio for ${target.hanzi}`} size={40} />
                        </Stack>

                        {shows(exercise, 'english') && target.english && (
                            <Box sx={{ mt: 2.5, p: 2.25, borderRadius: '16px', backgroundColor: SURFACE_SOFT }}>
                                <Typography sx={{ fontFamily: INTER, fontSize: 14, color: NAVY }}>{target.english}</Typography>
                            </Box>
                        )}
                        {shows(exercise, 'info') && exercise.infoText && <InfoExpander text={exercise.infoText} />}
                    </Box>

                    <Box sx={desktop ? { pl: 3, borderLeft: `1px solid ${RING_TRACK}` } : undefined}>
                        {desktop && (
                            <Typography sx={{ fontFamily: INTER, fontWeight: 700, fontSize: 11, letterSpacing: '.08em', color: SLATE }}>
                                YOUR TURN
                            </Typography>
                        )}
                        <RecordAnswerPanel
                            grades={target.grades}
                            takes={target.takes}
                            scoring={exercise.graded ? 'graded' : 'feedback'}
                            correctText={exercise.teacher?.spokenCorrect}
                            incorrectText={exercise.teacher?.spokenIncorrect}
                            onComplete={() => onReady('done')}
                            onSkippable={() => onReady('skip')}
                        />
                    </Box>
                </Box>
                <ExerciseSupport exercise={exercise} />
            </Box>
        </Box>
    );
}
